import { defaultApiState } from '@/store/types/general.types';
import { AxiosError } from 'axios';
import {
  FHIRClient,
  FHIRClientDownloadPermission,
  FHIRClientParameterPermission,
  FHIRDefaultParameter,
  FHIRDownloadSettings,
  FHIRListValue,
  FHIRState,
  GetFHIRClientsResponsePayload
} from './types/fhir.types';

function getCombinedAccessChosen(
  listValues: Pick<FHIRListValue, 'accessChosen'>[]
): 'all_clients' | 'mixed' | 'configured' {
  if (listValues.every((lv) => lv.accessChosen === 'all_clients')) {
    return 'all_clients';
  }
  if (listValues.every((lv) => lv.accessChosen === 'configured')) {
    return 'configured';
  }
  return 'mixed';
}

export const fhirMutations = {
  GET_DEFAULT_PARAMETERS_LOADING(state: FHIRState) {
    state.apiState.getDefaultParameters = { ...defaultApiState, loading: true };
  },
  GET_DEFAULT_PARAMETERS_SUCCESS(
    state: FHIRState,
    payload: FHIRDefaultParameter[]
  ) {
    state.apiState.getDefaultParameters = { ...defaultApiState, success: true };
    state.defaultParameters = payload;
  },
  GET_DEFAULT_PARAMETERS_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.getDefaultParameters = { ...defaultApiState, error };
  },

  UPDATE_DEFAULT_PARAMETER_LOADING(state: FHIRState) {
    state.apiState.updateDefaultParameter = {
      ...defaultApiState,
      loading: true
    };
  },
  UPDATE_DEFAULT_PARAMETER_SUCCESS(
    state: FHIRState,
    updated: FHIRDefaultParameter
  ) {
    state.apiState.updateDefaultParameter = {
      ...defaultApiState,
      success: true
    };
    if (state.defaultParameters) {
      const idx = state.defaultParameters.findIndex((p) => p.id === updated.id);
      if (idx !== -1) state.defaultParameters.splice(idx, 1, updated);
    }
  },
  UPDATE_DEFAULT_PARAMETER_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.updateDefaultParameter = { ...defaultApiState, error };
  },

  UPDATE_DEFAULT_PARAMETER_LIST_VALUES_LOADING(state: FHIRState) {
    state.apiState.updateDefaultParameterListValues = {
      ...defaultApiState,
      loading: true
    };
  },
  UPDATE_DEFAULT_PARAMETER_LIST_VALUES_SUCCESS(
    state: FHIRState,
    payload: { parameterId: number; listValues: FHIRListValue[] }
  ) {
    state.apiState.updateDefaultParameterListValues = {
      ...defaultApiState,
      success: true
    };
    if (state.defaultParameters) {
      const param = state.defaultParameters.find(
        (p) => p.id === payload.parameterId
      );
      if (param) {
        param.listValues = payload.listValues;
        param.accessChosen = getCombinedAccessChosen(payload.listValues);
      }
    }
  },
  UPDATE_DEFAULT_PARAMETER_LIST_VALUES_ERROR(
    state: FHIRState,
    error: AxiosError
  ) {
    state.apiState.updateDefaultParameterListValues = {
      ...defaultApiState,
      error
    };
  },

  GET_DEFAULT_DOWNLOAD_PERMISSIONS_LOADING(state: FHIRState) {
    state.apiState.getDefaultDownloadPermissions = {
      ...defaultApiState,
      loading: true
    };
  },
  GET_DEFAULT_DOWNLOAD_PERMISSIONS_SUCCESS(
    state: FHIRState,
    payload: FHIRDownloadSettings
  ) {
    state.apiState.getDefaultDownloadPermissions = {
      ...defaultApiState,
      success: true
    };
    state.downloadSettings = payload;
  },
  GET_DEFAULT_DOWNLOAD_PERMISSIONS_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.getDefaultDownloadPermissions = {
      ...defaultApiState,
      error
    };
  },

  UPDATE_DOWNLOAD_SETTINGS_LOADING(state: FHIRState) {
    state.apiState.updateDownloadSettings = {
      ...defaultApiState,
      loading: true
    };
  },
  UPDATE_DOWNLOAD_SETTINGS_SUCCESS(
    state: FHIRState,
    payload: FHIRDownloadSettings
  ) {
    state.apiState.updateDownloadSettings = {
      ...defaultApiState,
      success: true
    };
    state.downloadSettings = payload;
  },
  UPDATE_DOWNLOAD_SETTINGS_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.updateDownloadSettings = { ...defaultApiState, error };
  },

  GET_FHIR_CLIENTS_LOADING(state: FHIRState) {
    state.apiState.getFhirClients = { ...defaultApiState, loading: true };
  },
  GET_FHIR_CLIENTS_SUCCESS(
    state: FHIRState,
    payload: GetFHIRClientsResponsePayload
  ) {
    state.apiState.getFhirClients = { ...defaultApiState, success: true };
    state.fhirClients = payload;
  },
  GET_FHIR_CLIENTS_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.getFhirClients = { ...defaultApiState, error };
  },

  GET_FHIR_CLIENT_BY_ID_LOADING(state: FHIRState) {
    state.apiState.getFhirClientById = { ...defaultApiState, loading: true };
  },
  GET_FHIR_CLIENT_BY_ID_SUCCESS(state: FHIRState, payload: FHIRClient) {
    state.apiState.getFhirClientById = { ...defaultApiState, success: true };
    state.fhirClient = payload;
  },
  GET_FHIR_CLIENT_BY_ID_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.getFhirClientById = { ...defaultApiState, error };
  },

  CREATE_FHIR_CLIENT_LOADING(state: FHIRState) {
    state.apiState.createFhirClient = { ...defaultApiState, loading: true };
  },
  CREATE_FHIR_CLIENT_SUCCESS(state: FHIRState, _payload: FHIRClient) {
    state.apiState.createFhirClient = { ...defaultApiState, success: true };
  },
  CREATE_FHIR_CLIENT_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.createFhirClient = { ...defaultApiState, error };
  },

  UPDATE_FHIR_CLIENT_LOADING(state: FHIRState) {
    state.apiState.updateFhirClient = { ...defaultApiState, loading: true };
  },
  UPDATE_FHIR_CLIENT_SUCCESS(state: FHIRState, payload: FHIRClient) {
    state.apiState.updateFhirClient = { ...defaultApiState, success: true };
    if (state.fhirClient?.id === payload.id) {
      // payload may omit relations updateFhirClient doesn't load (eg.
      // parameterPermission/downloadParameterPermission); merge rather than
      // replace so those aren't clobbered when saved alongside them.
      state.fhirClient = { ...state.fhirClient, ...payload };
    }
    if (state.fhirClients) {
      const idx = state.fhirClients.items.findIndex((c) => c.id === payload.id);
      if (idx !== -1) state.fhirClients.items.splice(idx, 1, payload);
    }
  },
  UPDATE_FHIR_CLIENT_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.updateFhirClient = { ...defaultApiState, error };
  },

  DELETE_FHIR_CLIENT_LOADING(state: FHIRState) {
    state.apiState.deleteFhirClient = { ...defaultApiState, loading: true };
  },
  DELETE_FHIR_CLIENT_SUCCESS(state: FHIRState, payload: FHIRClient) {
    state.apiState.deleteFhirClient = { ...defaultApiState, success: true };
    if (state.fhirClient?.id === payload.id) {
      state.fhirClient = undefined;
    }
    if (state.fhirClients) {
      state.fhirClients.items = state.fhirClients.items.filter(
        (c) => c.id !== payload.id
      );
      state.fhirClients.totalItems -= 1;
      state.fhirClients.itemCount -= 1;
    }
  },
  DELETE_FHIR_CLIENT_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.deleteFhirClient = { ...defaultApiState, error };
  },

  UPDATE_CLIENT_PARAMETERS_LOADING(state: FHIRState) {
    state.apiState.updateClientParameters = {
      ...defaultApiState,
      loading: true
    };
  },
  UPDATE_CLIENT_PARAMETERS_SUCCESS(
    state: FHIRState,
    payload: FHIRClientParameterPermission[]
  ) {
    state.apiState.updateClientParameters = {
      ...defaultApiState,
      success: true
    };
    if (state.fhirClient) {
      state.fhirClient.parameterPermission = payload;
    }
  },
  UPDATE_CLIENT_PARAMETERS_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.updateClientParameters = { ...defaultApiState, error };
  },

  UPDATE_CLIENT_DOWNLOAD_LOADING(state: FHIRState) {
    state.apiState.updateClientDownload = {
      ...defaultApiState,
      loading: true
    };
  },
  UPDATE_CLIENT_DOWNLOAD_SUCCESS(
    state: FHIRState,
    payload: FHIRClientDownloadPermission
  ) {
    state.apiState.updateClientDownload = {
      ...defaultApiState,
      success: true
    };
    if (state.fhirClient) {
      state.fhirClient.downloadParameterPermission = payload;
    }
  },
  UPDATE_CLIENT_DOWNLOAD_ERROR(state: FHIRState, error: AxiosError) {
    state.apiState.updateClientDownload = { ...defaultApiState, error };
  }
};
